/* eslint-disable @typescript-eslint/no-explicit-any -- legacy untyped resource: responses are passed through as the server sends them. */
import { SdkContext } from '../internal'
import { withAnalyticsSession } from '@/lib/analytics/session'
import { listItems } from '@/lib/utils'

/** F-39: downsell offers (owner and admin) and their impressions (the buyer, or an e-mail token). */
export class DownsellResource {
  constructor(private ctx: SdkContext) {}

  private offersPath = '/subscription-groups/{groupId}/downsell-offers'

  async listOffers(groupId: string): Promise<any[]> {
    return listItems(await this.ctx.raw({ httpMethod: 'GET', path: this.offersPath }, { groupId }))
  }

  async createOffer(groupId: string, body: any): Promise<any> {
    return this.ctx.raw({ httpMethod: 'POST', path: this.offersPath, hasBody: true }, { groupId, body })
  }

  async updateOffer(groupId: string, offerId: string, body: any): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'PUT', path: `${this.offersPath}/{offerId}`, hasBody: true },
      { groupId, offerId, body }
    )
  }

  async deleteOffer(groupId: string, offerId: string): Promise<any> {
    return this.ctx.raw({ httpMethod: 'DELETE', path: `${this.offersPath}/{offerId}` }, { groupId, offerId })
  }

  /** Available impressions of the signed-in member, optionally of one plan subscription. */
  async listImpressions(subscriptionId?: string): Promise<any[]> {
    return listItems(
      await this.ctx.raw(
        { httpMethod: 'GET', path: '/downsell-impressions' },
        subscriptionId ? { query: { subscriptionId } } : {}
      )
    )
  }

  async markSeen(id: string): Promise<any> {
    return this.ctx.raw({ httpMethod: 'POST', path: '/downsell-impressions/{id}/seen', hasBody: true }, { id, body: {} })
  }

  async quote(id: string, body: { couponCode?: string; paymentMethod?: string; installments?: number } = {}): Promise<any> {
    return this.ctx.raw({ httpMethod: 'POST', path: '/downsell-impressions/{id}/quote', hasBody: true }, { id, body })
  }

  async accept(id: string, body: Record<string, unknown>): Promise<any> {
    return this.ctx.raw({ httpMethod: 'POST', path: '/downsell-impressions/{id}/accept', hasBody: true }, { id, body: withAnalyticsSession(body) })
  }

  async decline(id: string): Promise<any> {
    return this.ctx.raw({ httpMethod: 'POST', path: '/downsell-impressions/{id}/decline', hasBody: true }, { id, body: {} })
  }

  /* The e-mail link: no session, the token is the key. A GET has no effect. */
  async getByToken(token: string): Promise<any> {
    return this.ctx.raw({ httpMethod: 'GET', path: '/downsell-impressions/by-token/{token}' }, { token })
  }

  async markSeenByToken(token: string): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'POST', path: '/downsell-impressions/by-token/{token}/seen', hasBody: true },
      { token, body: {} }
    )
  }

  /** "No, thanks" from the e-mail link (dc03274): the offer is declined and not shown again. */
  async declineByToken(token: string): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'POST', path: '/downsell-impressions/by-token/{token}/decline', hasBody: true },
      { token, body: {} }
    )
  }

  async acceptByToken(token: string, paymentMethod?: string): Promise<any> {
    return this.ctx.raw(
      { httpMethod: 'POST', path: '/downsell-impressions/by-token/{token}/accept', hasBody: true },
      { token, body: withAnalyticsSession(paymentMethod ? { paymentMethod } : {}) }
    )
  }
}
